/**
 * Champs à menu déroulant (« Modification 1 » : « quand tu peux, fais des entrées à menu déroulant »).
 * `ListeOuAutre` : une liste fermée, plus « Autre… » qui ouvre un champ libre — la liste guide sans enfermer.
 * `ChoixMultiples` : des pastilles à cocher (domaines d'intervention…).
 */
import { useId, useState } from "react";
import { Check, Plus, X } from "lucide-react";
import { AUTRE_DOMAINE, DOMAINES_EXPERTISE, type LigneDomaine } from "@/domaine/candidature/questionnaire";
import { cx, Selecteur } from "./base";

const AUTRE = "__autre__";

export function ListeOuAutre({
  libelle,
  options,
  value,
  onChange,
  aide,
  erreur,
  vide = "— Choisir —",
  autre = true,
  disabled,
}: {
  libelle: string;
  options: readonly string[];
  value: string;
  onChange: (v: string) => void;
  aide?: string;
  erreur?: string;
  vide?: string;
  autre?: boolean;
  disabled?: boolean;
}) {
  const horsListe = value !== "" && !options.includes(value);
  const [libre, setLibre] = useState(horsListe);
  const idLibre = useId();
  return (
    <div className="min-w-0 space-y-2">
      <Selecteur
        libelle={libelle}
        aide={libre ? undefined : aide}
        erreur={libre ? undefined : erreur}
        disabled={disabled}
        value={libre ? AUTRE : value}
        onChange={(e) => {
          if (e.target.value === AUTRE) {
            setLibre(true);
            onChange("");
          } else {
            setLibre(false);
            onChange(e.target.value);
          }
        }}
      >
        <option value="">{vide}</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
        {autre && <option value={AUTRE}>Autre… (saisir)</option>}
      </Selecteur>
      {libre && (
        <div>
          <label htmlFor={idLibre} className="sr-only">
            {libelle} — précisez
          </label>
          <input
            id={idLibre}
            autoFocus
            disabled={disabled}
            value={value}
            placeholder="Précisez…"
            onChange={(e) => onChange(e.target.value)}
            className="h-10 w-full rounded-sm border border-trait-fort bg-carte px-3 text-sm focus:border-accent focus:ring-2 focus:ring-accent/20 focus:outline-none"
          />
          {(erreur || aide) && <p className={cx("mt-1.5 text-[13px]", erreur ? "text-danger" : "text-encre-3")}>{erreur ?? aide}</p>}
        </div>
      )}
    </div>
  );
}

export function ChoixMultiples({ libelle, options, valeurs, onChange, aide, max = 20 }: { libelle: string; options: readonly string[]; valeurs: string[]; onChange: (v: string[]) => void; aide?: string; max?: number }) {
  return (
    <fieldset className="min-w-0">
      <legend className="mb-1.5 block text-[13px] font-medium text-encre-2">{libelle}</legend>
      <div className="flex flex-wrap gap-1.5">
        {options.map((o) => {
          const actif = valeurs.includes(o);
          return (
            <button
              key={o}
              type="button"
              aria-pressed={actif}
              disabled={!actif && valeurs.length >= max}
              onClick={() => onChange(actif ? valeurs.filter((x) => x !== o) : [...valeurs, o])}
              className={cx(
                "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[13px] transition-colors duration-150 disabled:opacity-40",
                actif ? "border-accent bg-accent-doux font-medium text-accent-fort" : "border-trait-fort bg-carte text-encre-2 hover:border-accent",
              )}
            >
              {actif && <Check className="size-3.5" strokeWidth={3} aria-hidden />}
              {o}
            </button>
          );
        })}
      </div>
      {aide && <p className="mt-1.5 text-[13px] text-encre-3">{aide}</p>}
    </fieldset>
  );
}

/** Convertit une saisie française (« 3,5 ») en nombre, ou `null` si vide. */
export const nombreSaisi = (x: string | number | null | undefined): number | null => {
  const t = String(x ?? "").trim().replace(",", ".").replace(/\s/g, "");
  if (t === "") return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
};

/** Euros saisis → centimes (entier), ou `null`. */
export const centimesSaisis = (x: string): number | null => {
  const n = nombreSaisi(x);
  return n === null ? null : Math.round(n * 100);
};

export const eurosEnSaisie = (c: number | null | undefined): string =>
  c === null || c === undefined ? "" : String(c / 100).replace(".", ",");

/**
 * Domaine d'expertise du formateur (31/10/2026) : un MENU DÉROULANT par domaine, liste fermée (catégories du catalogue
 * de la vitrine) + « Autre » avec précision. « Ajouter un domaine » ouvre une ligne de plus (choix multiple conservé).
 * Une valeur enregistrée hors liste s'affiche comme « Autre » avec sa valeur : rien n'est perdu.
 */
export function ChampDomaines({
  lignes,
  onChange,
  erreur,
  disabled,
  max = 6,
  idPremier,
}: {
  lignes: LigneDomaine[];
  onChange: (l: LigneDomaine[]) => void;
  erreur?: string;
  disabled?: boolean;
  max?: number;
  idPremier?: string;
}) {
  const visibles = lignes.length > 0 ? lignes : [{ choix: "", precision: "" }];
  const maj = (i: number, l: Partial<LigneDomaine>) =>
    onChange(visibles.map((x, j) => (j === i ? { ...x, ...l } : x)));
  return (
    <div className="min-w-0 space-y-3">
      {visibles.map((l, i) => (
        <div key={i} className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] sm:items-end">
          <Selecteur
            id={i === 0 ? idPremier : undefined}
            libelle={i === 0 ? "Domaine d'expertise" : `Autre domaine d'expertise (${i + 1})`}
            required={i === 0}
            erreur={i === 0 ? erreur : undefined}
            disabled={disabled}
            value={l.choix}
            onChange={(e) =>
              maj(i, {
                choix: e.target.value,
                precision: e.target.value === AUTRE_DOMAINE ? l.precision : "",
              })
            }
          >
            <option value="">— Choisir —</option>
            {DOMAINES_EXPERTISE.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </Selecteur>
          {l.choix === AUTRE_DOMAINE ? (
            <div>
              <label
                className="mb-1.5 block text-[13px] font-medium text-encre-2"
                htmlFor={`domaine-precision-${i}`}
              >
                Précisez le domaine
              </label>
              <input
                id={`domaine-precision-${i}`}
                value={l.precision}
                maxLength={120}
                disabled={disabled}
                placeholder="Ex. ferroviaire, œnologie…"
                onChange={(e) => maj(i, { precision: e.target.value })}
                className="h-10 w-full rounded-sm border border-trait-fort bg-carte px-3 text-sm focus:border-accent focus:ring-2 focus:ring-accent/20 focus:outline-none"
              />
            </div>
          ) : (
            <span className="hidden sm:block" />
          )}
          {i > 0 && !disabled ? (
            <button
              type="button"
              onClick={() => onChange(visibles.filter((_, j) => j !== i))}
              className="inline-flex h-10 items-center justify-center gap-1.5 rounded-sm px-2 text-[13px] text-encre-3 hover:bg-papier-3 hover:text-danger"
              aria-label={`Retirer le domaine ${i + 1}`}
            >
              <X className="size-4" aria-hidden /> Retirer
            </button>
          ) : (
            <span className="hidden sm:block sm:w-[76px]" />
          )}
        </div>
      ))}
      {!disabled && visibles.length < max && (
        <button
          type="button"
          onClick={() => onChange([...visibles, { choix: "", precision: "" }])}
          className="inline-flex min-h-8 items-center gap-1.5 rounded-sm px-2 text-[13px] font-medium text-accent hover:bg-accent-doux"
        >
          <Plus className="size-4" aria-hidden /> Ajouter un domaine
        </button>
      )}
    </div>
  );
}
